const EXPLICIT_TIME_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

export function parseUtcTimestamp(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value !== "string") return null;

  const timestamp = value.trim();
  if (!timestamp || !EXPLICIT_TIME_ZONE.test(timestamp)) return null;

  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatUtcTime(value, { fallback = "—", milliseconds = false } = {}) {
  const date = parseUtcTimestamp(value);
  if (!date) return fallback;

  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  const fraction = milliseconds ? `.${String(date.getUTCMilliseconds()).padStart(3, "0")}` : "";
  return `${hours}:${minutes}:${seconds}${fraction} UTC`;
}

export function formatUtcDateTime(value, { fallback = "—", milliseconds = false } = {}) {
  const date = parseUtcTimestamp(value);
  if (!date) return fallback;

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day} ${formatUtcTime(date, { milliseconds })}`;
}

// Indian Standard Time (IST) offset: UTC + 05:30
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatIndianTime(
  value,
  { fallback = "—", milliseconds = false, includeSeconds = true } = {},
) {
  const date = parseUtcTimestamp(value);
  if (!date) return fallback;

  const istDate = new Date(date.getTime() + IST_OFFSET_MS);
  const hours24 = istDate.getUTCHours();
  const minutes = String(istDate.getUTCMinutes()).padStart(2, "0");
  const seconds = String(istDate.getUTCSeconds()).padStart(2, "0");
  const ampm = hours24 >= 12 ? "PM" : "AM";
  const hours12 = String(hours24 % 12 || 12).padStart(2, "0");
  const fraction = milliseconds ? `.${String(istDate.getUTCMilliseconds()).padStart(3, "0")}` : "";

  return includeSeconds
    ? `${hours12}:${minutes}:${seconds}${fraction} ${ampm} IST`
    : `${hours12}:${minutes} ${ampm} IST`;
}

export function formatIndianDate(value, { fallback = "—", includeYear = true } = {}) {
  const date = parseUtcTimestamp(value);
  if (!date) return fallback;

  const istDate = new Date(date.getTime() + IST_OFFSET_MS);
  const day = String(istDate.getUTCDate()).padStart(2, "0");
  const month = MONTH_NAMES[istDate.getUTCMonth()];
  const year = istDate.getUTCFullYear();
  return includeYear ? `${day} ${month} ${year}` : `${day} ${month}`;
}

export function formatIndianDateTime(value, { fallback = "—", milliseconds = false } = {}) {
  const date = parseUtcTimestamp(value);
  if (!date) return fallback;

  const dateStr = formatIndianDate(date, { fallback });
  const timeStr = formatIndianTime(date, { fallback, milliseconds });
  return `${dateStr}, ${timeStr}`;
}

export function getActiveTimezone() {
  try {
    return localStorage.getItem("ai_trader_tz") || "IST";
  } catch {
    return "IST";
  }
}

export function setActiveTimezone(tz) {
  try {
    localStorage.setItem("ai_trader_tz", tz);
  } catch {}
}

export function formatDisplayTime(
  value,
  { timezone = null, fallback = "—", milliseconds = false } = {},
) {
  const tz = timezone || getActiveTimezone();
  if (tz === "IST") {
    return formatIndianTime(value, { fallback, milliseconds });
  }
  return formatUtcTime(value, { fallback, milliseconds });
}

export function formatDisplayDateTime(
  value,
  { timezone = null, fallback = "—", milliseconds = false } = {},
) {
  const tz = timezone || getActiveTimezone();
  if (tz === "IST") {
    return formatIndianDateTime(value, { fallback, milliseconds });
  }
  return formatUtcDateTime(value, { fallback, milliseconds });
}

export function formatPriceWithCommas(value, precision = 4) {
  const num = finiteNumber(value);
  if (num == null) return "—";
  if (num >= 1000) {
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: precision,
    });
  }
  if (num < 1) {
    return num.toFixed(precision);
  }
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: precision,
  });
}

export function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function formatOperatorReason(label, rawReason, fallback = "Reason unavailable") {
  if (typeof label === "string" && label.trim()) return label.trim();
  if (typeof rawReason !== "string" || !rawReason.trim()) return fallback;

  const normalized = rawReason.trim().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
}
